/**
 * English translation strings for the Inhab™ Control application.
 * All user-facing labels, messages, headings, placeholders, and button text
 * are centralised here. Import using the `t()` utility from `utils/i18n`.
 */

const en = {
  // ─── Common / Shared ────────────────────────────────────────────────────────
  common: {
    copyright : '© Siemens 2026',
    loading: 'Loading...',
    loadingApp: 'Loading your application...',
    cancel: 'Cancel',
    apply: 'Apply changes',
    yes: 'Yes',
    no: 'No',
    ok: 'Ok',
    na: 'N/A',
    on: 'On',
    offline: 'Offline',
    standby: 'Standby',
    online: 'Online',
    refresh: 'Refresh',
    reset: 'Reset',
    delete: 'Delete',
    create: 'Create',
    proceed: 'Proceed',
    warning: 'Warning',
    currencyPrefix: '$ ',
    lastUpdatedPrefix: 'Last updated on ',
    connectionLost: 'Connection lost'
  },

  // ─── App Shell / Routes ──────────────────────────────────────────────────────
  suspense: {
    loading: 'Loading your application...'
  },

  // ─── Header ─────────────────────────────────────────────────────────────────
  header: {
    appName: 'Inhab™ Control',
    logoAlt: 's',
    separator: '|'
  },

  // ─── Footer ─────────────────────────────────────────────────────────────────
  footer: {
    privacyPolicy: 'Privacy Policy',
    termsOfUse: 'Terms of Use'
  },

  // ─── Sidebar / Navbar ────────────────────────────────────────────────────────
  sidebar: {
    home: 'Home',
    breakers: 'Breakers',
    schedule: 'Schedule',
    activity: 'Activity',
    settings: 'Settings',
    searchPlaceholder: 'Search..'
  },

  // ─── Breadcrumb ───────────────────────────────────────────────────────────────
  breadcrumb: {
    homepage: 'Homepage',
    gateways: 'Gateways'
  },

  // ─── Notification Panel ──────────────────────────────────────────────────────
  notification: {
    heading: 'Notification',
    markAllAsRead: 'Mark all as read',
    empty: 'No notifications.',
    readAll: 'Read All Notification'
  },

  // ─── Profile Dropdown ────────────────────────────────────────────────────────
  profile: {
    welcome: username => `Welcome, ${username}`,
    role: 'Admin',
    userAccount: 'User account',
    changePassword: 'Change password',
    support: 'Support',
    autoLogout: 'Auto Logout',
    autoLogoutActive: 'Active',
    autoLogoutDisabled: 'Disabled',
    logout: 'Logout'
  },

  // ─── Token Monitor ───────────────────────────────────────────────────────────
  tokenMonitor: {
    heading: 'Session Expiring Due to Inactivity',
    body: 'You have been idle for 1 hour. Session will expire soon. Please refresh to continue.',
    cancel: 'Cancel',
    refresh: 'Refresh'
  },

  // ─── Logout Flow ────────────────────────────────────────────────────────────
  logout: {
    loadingMessage: 'Please wait while we log you out.'
  },

  // ─── Idle Timer ──────────────────────────────────────────────────────────────
  idleTimer: {
    heading: 'Session will expire soon.',
    body: 'If the browser window is idle for 5 or more minutes, the session will expire automatically, and you will have to login again.',
    timeLeft: (mm, ss) => `Time left ${mm}:${ss}`,
    logout: 'Logout',
    resume: 'Resume'
  },

  // ─── Inline Notification ─────────────────────────────────────────────────────
  inlineNotification: {
    connectionLost: 'Connection lost',
    tooltipTitle: 'Connection lost',
    troubleshoot: 'Please follow the following to troubleshoot the issue:',
    step1: '1. Make sure that the Gateway is powered on.',
    step2: '2. Check the network connection.',
    refresh: 'Refresh'
  },

  // ─── Live Indicator ──────────────────────────────────────────────────────────
  liveIndicator: {
    live: 'Live'
  },

  // ─── Filter Panel ────────────────────────────────────────────────────────────
  filterPanel: {
    heading: 'Filters',
    resetAll: 'Reset all',
    applyFilter: 'Apply filter',
    resetSubFilter: 'Reset'
  },

  // ─── MultiSelect Dropdown ────────────────────────────────────────────────────
  multiSelectDropdown: {
    placeholder: 'Select...',
    noOptions: 'No options found',
    selected: count => `${count} selected`
  },

  // ─── MultiSelect List ────────────────────────────────────────────────────────
  multiSelectList: {
    searchPlaceholder: 'Search...',
    noLabel: 'No Label'
  },

  // ─── Action Dialog ────────────────────────────────────────────────────────────
  actionDialog: {
    defaultYes: 'Yes',
    defaultNo: 'No'
  },

  // ─── Login Page ───────────────────────────────────────────────────────────────
  login: {
    brand: 'Siemens',
    appTitle: 'Inhab',
    appTitleSuperscript: 'TM',
    appTitleSuffix: 'Control',
    notAuthenticated: 'You are not authenticated. Please login!',
    loginButton: 'Login',
    loggingIn: 'Logging you in...'
  },

  // ─── Not Found Page ───────────────────────────────────────────────────────────
  notFound: {
    message: 'Oops! The Page could not found!'
  },

  // ─── Schedule Page ────────────────────────────────────────────────────────────
  schedule: {
    noSchedule: 'No Schedule Available'
  },

  // ─── Activity Page ────────────────────────────────────────────────────────────
  activity: {
    pageTitle: 'Activity',
    filterButton: 'Filter',
    downloadButton: 'Download',
    maintenanceHeading: 'Page Under Maintenance',
    maintenanceBody: "This page is currently unavailable. We're working on it and will be back soon. Thank you for your patience."
  },

  // ─── Activity Grid ────────────────────────────────────────────────────────────
  activityGrid: {
    breakerName: 'Breaker Name',
    trippedDateTime: 'Tripped Date-Time',
    resetDateTime: 'Reset Date-Time',
    downTime: 'Down Time (hh:mm)',
    faultCategory: 'Fault Category',
    switchedTo: 'Switched To',
    dateTime: 'Date-Time',
    activity: 'Activity',
    switchedOn: 'On',
    switchedOffline: 'Offline',
    switchedStandby: 'Off',
    tooltipTitle: 'Breaker states',
    tooltipOnDesc: 'Breaker was set to On.',
    tooltipStandbyDesc: 'Breaker was turned Off.',
    tooltipOfflineDesc: 'Breaker went offline.',
    logsLastMonth: 'Logs of last 1 month',
    loading: 'Loading...'
  },

  // ─── Download Modal ───────────────────────────────────────────────────────────
  downloadModal: {
    download: 'Download',
    heading: 'Download logs',
    subheading: 'Document will be downloaded in .csv format.',
    period: 'Period*',
    from: 'From*',
    to: 'To*',
    cancel: 'Cancel'
  },

  // ─── Activity Filter ─────────────────────────────────────────────────────────
  activityFilter: {
    breakersDevices: 'Breakers/ Devices',
    dateRange: 'Date Range',
    faultCategory: 'Fault Category'
  },

  // ─── Home Page ────────────────────────────────────────────────────────────────
  home: {
    greeting: name => `Hello ${name}`,
    connectionLost: 'Connection lost',
    noAvailableChart: 'No available chart',
    failedToRetrieve: 'Failed to retrieve data',
    upcomingSchedule: 'Upcoming Schedule',
    totalEnergyLabel: 'Total energy consumption | '
  },

  // ─── Breaker Dashboard ────────────────────────────────────────────────────────
  breakerDashboard: {
    breakerDetails: 'Breaker Details',
    connectionLost: 'Connection lost',
    upcomingSchedule: 'Upcoming Schedule',
    energyConsumption: 'Energy Consumption'
  },

  // ─── Breaker Chart Tab ────────────────────────────────────────────────────────
  breakerChartTab: {
    voltageCurrent: 'Voltage-Current',
    noAvailableGraph: 'No available graph',
    offlineBody: 'The breaker is currently in Offline. Please turn it On to enable real-time monitoring.',
    live: 'Live',
    energyConsumption: 'Energy Consumption',
    reset: 'Reset'
  },

  // ─── Device Details Card ──────────────────────────────────────────────────────
  deviceDetailsCard: {
    heading: 'Device details',
    slotId: 'Slot ID',
    na: 'N/A'
  },

  // ─── Device State Card ────────────────────────────────────────────────────────
  deviceStateCard: {
    heading: 'Device State',
    tooltipTitle: 'Breaker States',
    tooltipOnDesc: 'The breaker is supplying power.',
    tooltipStandbyDesc: 'The breaker is not supplying power.',
    tooltipOfflineDesc:
      'The breaker state is unknown. It may have tripped, handle switched OFF or lost communication.',
    offline: 'Offline',
    on: 'On',
    standby: 'Off',
    headingOn: 'Turn on breaker',
    headingOff: 'Turn off breaker',
    actionOn: 'Turn on',
    actionOff: 'Turn off',
    confirmStandby: 'Turning this circuit off in the app will stop supplying power to connected devices. Do you want to continue?',
    confirmOn: 'Turning this circuit on will supply power to all connected devices. Do you want to continue?',
    AccordionHeading:'WARNING: Electric Shock Hazard',
    AccordionText:'Turning the breaker OFF via the app does not completely disconnect power. To avoid death or serious injury, always switch the breaker OFF at the physical panel before performing any electrical work.'
  },

  // ─── Schedule Card ────────────────────────────────────────────────────────────
  scheduleCard: {
    noData: 'No data available',
    collapse: 'Collapse',
    more: 'More',
    weekdaysMorning: 'Weekdays - Morning',
    weekdaysEvening: 'Weekdays - Evening',
    weekendsMorning: 'Weekends - Morning',
    weekendsEvening: 'Weekends - Evening'
  },

  // ─── Edit Breaker Modal ───────────────────────────────────────────────────────
  editBreakerModal: {
    heading: 'Edit breaker details',
    loading: 'Loading....',
    breakerNameLabel: '* Breaker name',
    breakerNamePlaceholder: 'Enter breaker name',
    validRequired: 'Breaker name is required.',
    validMin: 'Breaker name must be at least 4 characters.',
    validMax: 'Breaker name must not exceed 29 characters.',
    validAscii: 'Only ASCII characters (0x20-0x7e) are allowed',
    breakerIconLabel: '* Breaker icon',
    addToGroup: 'Add to a group',
    addEndDevices: 'Add end devices',
    gatewayName: 'Gateway Name',
    slotId: 'Slot ID',
    firmwareVersion: 'Firmware version',
    hardwareVersion: 'Hardware version',
    cancel: 'Cancel',
    applyChanges: 'Apply changes'
  },

  // ─── Breakers Page ────────────────────────────────────────────────────────────
  breakers: {
    connectionLost: 'Connection lost',
    loading: 'Loading...',
    noBreakers: 'No breakers found.',
    leftSide: 'Left side',
    rightSide: 'Right side'
  },

  // ─── Breakers Table ───────────────────────────────────────────────────────────
  breakersTable: {
    name: 'Name',
    leftSide: 'Left side',
    rightSide: 'Right side',
    slotId: 'Slot ID',
    state: 'State',
    tooltipTitle: 'Breaker States',
    tooltipOnDesc: 'The breaker is supplying power.',
    tooltipStandbyDesc: 'The breaker is off and not supplying power.',
    tooltipOfflineDesc:
      'The breaker state is unknown. It may have tripped, handle switched OFF or lost communication.',
    warning: 'WARNING',
    electricShockHazard: 'Electric Shock Hazard',
    electricHazardDescription:
      'Turning the breaker OFF via the app does not completely disconnect power. To avoid death or serious injury, always switch the breaker OFF at the physical panel before performing any electrical work.',
    electricHazardDescriptionPart1: 'Turning the breaker OFF via the app does',
    electricHazardDescriptionNot: 'not',
    electricHazardDescriptionPart2:
      'completely disconnect power. To avoid death or serious injury, always switch the breaker OFF at the',
    electricHazardDescriptionPhysicalPanel: 'physical panel',
    electricHazardDescriptionPart3: 'before performing any electrical work.',
    noBreakers: 'No breakers found.'
  },

  // ─── Breaker Table Row ────────────────────────────────────────────────────────
  breakerTableRow: {
    deviceOnline: 'Device Online',
    deviceOffline: 'Device Offline',
    confirmStandby: 'Are you sure you want to turn the breaker off?',
    confirmOn: 'Are you sure you want to turn the breaker on?'
  },

  // ─── Settings Page ────────────────────────────────────────────────────────────
  settings: {
    pageTitle: 'Settings',
    tabs: {
      account: 'Account',
      manageGateways: 'Manage Gateways',
      aboutInhab: 'About Inhab',
      products: 'Products',
      support: 'Support'
    }
  },

  // ─── About Inhab ──────────────────────────────────────────────────────────────
  aboutInhab: {
    heading: 'About',
    subheading: 'Inhab™ Control',
    description1:
      'Inhab™ Intelligent Habitat products are the foundation for smart living...',
    description2:
      'Inhab™ Intelligent Habitat products are the foundation for smart living...',
    learnMore: 'Click here to know more',
    controlHeading: 'About Inhab™ Control',
    accordionItems: [
      { heading: 'Corporate information', content: 'Content coming soon.' },
      { heading: 'Privacy notice', content: 'Content coming soon.' },
      {
        heading: 'Terms of use',
        content:
          'Tortor fermentum ut auctor leo sed ac condimentum molestie. Non viverra quam purus, arcu a nibh sit lacus non. Ultrices non tempus semper velit. Donec mauris, nisl enim, nunc tellus risus netus.'
      },
      { heading: 'OSS licenses', content: 'Content coming soon.' }
    ]
  },

  // ─── Other Products ───────────────────────────────────────────────────────────
  otherProducts: {
    heading: 'Other Siemens Products',
    description:
      'Siemens residential power distribution products keep people safe and help reduce costs when building or renovating homes...',
    learnMore: 'Click here to know more',
    contactUs: 'Contact us to learn more about our products',
    imageAlt: 'Siemens Home Construction'
  },

  // ─── Support Page ─────────────────────────────────────────────────────────────
  support: {
    heading: 'Support',
    faqs: [
      {
        question: 'How do I rename a Gateway?',
        answer: 'To rename a Gateway, navigate to the Manage Gateway section and click the edit icon.'
      },
      {
        question: 'How many breakers can I attach on one Gateway load center?',
        answer: 'You can attach up to 53 breakers depending on the model and load capacity.'
      },
      {
        question: 'How do I identify the slot ID of a breaker?',
        answer:
          'Tortor fermentum ut auctor leo sed ac condimentum molestie...'
      },
      {
        question: 'How do I check the fault categories?',
        answer: 'Fault categories can be found under the Diagnostics tab within each breaker.'
      },
      {
        question: 'How can I schedule an action for a breaker?',
        answer:
          'Go to the Scheduler and add a new task with the target breaker and desired action.'
      }
    ]
  },

  // ─── User Account ─────────────────────────────────────────────────────────────
  userAccount: {
    heading: 'Account',
    loadingDetails: 'Loading User Details',
    usernameLabel: 'Username',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    na: 'N/A',
    logoutWarningHeading: 'Warning',
    logoutWarningBody: 'Are you sure you want to logout?',
    deleteAccountButton: 'Delete Account',
    deleteAccountWarningHeading: 'Delete Account',
    deleteAccountWarningBody: 'Are you sure you want to permanently delete your account? This action cannot be undone.',
    deleteAccountSuccessHeading: 'Account Deleted',
    deleteAccountSuccessBody: 'Your account has been successfully deleted.',
    deleteAccountFailureHeading: 'Delete Failed',
    deleteAccountFailureBody: 'We could not delete your account. Please try again or contact support.'
  },

  // ─── User Account Modal ───────────────────────────────────────────────────────
  userAccountModal: {
    loading: 'Loading...',
    editName: {
      title: 'Edit user name',
      firstNameLabel: 'First Name *',
      firstNamePlaceholder: 'Enter first name',
      firstNameRequired: 'First name is required',
      firstNameMin: 'Minimum 3 characters required',
      firstNameMax: 'Maximum 15 characters allowed',
      firstNamePattern: 'Only alphabets and spaces are allowed',
      lastNameLabel: 'Last Name *',
      lastNamePlaceholder: 'Enter last name',
      lastNameRequired: 'Last name is required',
      lastNameMin: 'Minimum 3 characters required',
      lastNameMax: 'Maximum 15 characters allowed',
      lastNamePattern: 'Only alphabets and spaces are allowed',
      applyButton: 'Apply changes'
    },
    changePassword: {
      title: 'Change password',
      subtitle:
        'On successful resetting of the password, you will be logged out and will have to login with the new password.',
      currentPasswordLabel: 'Current password*',
      currentPasswordPlaceholder: 'Enter current password',
      currentPasswordRequired: 'Current password is required',
      currentPasswordMax: 'Password cannot exceed 64 characters',
      forgotPassword: 'Forgot Password',
      newPasswordLabel: 'New password*',
      newPasswordPlaceholder: 'Enter new password',
      newPasswordRequired: 'New password is required',
      newPasswordMax: 'Password cannot exceed 64 characters',
      newPasswordLength: 'Password must be between 8 and 64 characters',
      newPasswordStrength: 'The password must have at least 3 of the following: lowercase, uppercase, digit, symbol',
      confirmPasswordLabel: 'Confirm new password*',
      confirmPasswordPlaceholder: 'Confirm new password',
      confirmPasswordRequired: 'Please confirm your password',
      confirmPasswordMismatch: 'Passwords do not match',
      resetButton: 'Reset password'
    },
    forgotPassword: {
      title: 'Forgot password',
      body: 'You will be logged out. Enter your email on the next screen to reset your password if we find a match.',
      proceedButton: 'Proceed'
    },
    cancelButton: 'Cancel'
  },

  // ─── Password Success Modal ───────────────────────────────────────────────────
  passwordSuccess: {
    successHeader: 'Password updated',
    failureHeader: 'Update failed',
    successContent:
      'Your password has been changed successfully. Please use your new password to log in.',
    failureContent:
      'We could not update your password. Please ensure that your current password is correct and try again. If the issue persists, contact support.',
    okButton: 'Ok'
  },

  // ─── Username Success Modal ───────────────────────────────────────────────────
  usernameSuccess: {
    successHeader: 'User name updated',
    failureHeader: 'Update failed',
    successContent:
      'Your user name has been updated successfully. You will be logged out now. Please log in again.',
    failureContent:
      'We could not update your user name. Please try again or contact support.',
    okButton: 'Ok'
  },

  // ─── Forgot Password Info Modal ───────────────────────────────────────────────
  forgotPasswordInfo: {
    title: 'Forgot password',
    body: "You will be logged out of the account. You will then need to enter the email address you use with this account and we'll then start the password reset process if we find a match.",
    cancelButton: 'Cancel',
    proceedButton: 'Proceed'
  },

  // ─── Edit User Account ────────────────────────────────────────────────────────
  editUserAccount: {
    dialogHeader: 'Edit user account details',
    fullNameLabel: 'Full name *',
    emailLabel: 'Email *',
    passwordLabel: 'Password *',
    cancelButton: 'Cancel',
    applyButton: 'Apply changes'
  },

  // ─── Utility Rates ────────────────────────────────────────────────────────────
  utilityRates: {
    heading: 'Utility rates',
    rateLabel: 'Rate per KWh*',
    currencyLabel: 'Currency*',
    mockValue: '$ USD'
  },

  // ─── Edit Utility Rate Modal ──────────────────────────────────────────────────
  editUtilityRate: {
    dialogHeader: 'Edit Utility Rates',
    loading: 'Loading...',
    rateLabel: '* Rate per kWh',
    ratePlaceholder: 'Enter rate',
    rateRequired: 'Rate is required.',
    ratePattern: 'Enter 1 digit before and up to 3 digits after the decimal',
    currencyLabel: '* Currency',
    updateError: 'Failed to update utility rate. Please try again.',
    cancelButton: 'Cancel',
    applyButton: 'Apply changes'
  },

  // ─── Manage Gateways ──────────────────────────────────────────────────────────
  manageGateways: {
    pageHeading: 'Manage Gateways',
    loading: 'Loading Gateways',
    noGateways: 'No more gateways found for the current user',
    contextMenu: {
      rename: 'Rename',
      delete: 'Delete'
    },
    sections: {
      gatewayDetails: 'Gateway Details',
      gatewayStatus: 'Gateway Status',
      manageDevices: 'Manage Devices',
      utilityRate: 'Utility Rate',
      emailNotifications: 'Email Notifications',
      manageGroups: 'Manage Groups'
    },
    gatewayDetails: {
      serialNumber: 'Serial number',
      firmwareVersion: 'Firmware version',
      hardwareVersion: 'Hardware version',
      model: 'Model',
      updateButton: 'Update'
    },
    gatewayStatus: {
      online: 'Online',
      offline: 'Offline'
    },
    manageDevices: {
      noDevices: 'No Devices Found'
    },
    emailNotifications: {
      monthlyConsumption: 'Monthly consumption summary',
      gatewayStatusChange: 'Gateway status change',
      breakerStateChange: 'Breaker state change'
    },
    manageGroups: {
      groupNameHeader: 'Group Name',
      breakersHeader: 'Breakers',
      createNew: 'Create New',
      noGroups: 'No Groups Found'
    }
  },

  // ─── Edit / Delete Gateway Modal ──────────────────────────────────────────────
  editManageGateway: {
    loading: 'Loading...',
    deleteConfirmPrefix: 'You are about to delete ',
    deleteConfirmSuffix:
      ' this action will revoke all access to the usage data, ability to monitor and control the Gateway. Are you sure?',
    gatewayNameLabel: '* Gateway name',
    gatewayNamePlaceholder: 'Enter Gateway name',
    gatewayNameRequired: 'Gateway name is required',
    gatewayNameMin: 'Minimum 3 characters required',
    gatewayNameMax: 'Maximum 29 characters allowed',
    gatewayNameAscii: 'Only ASCII characters (0x20-0x7e) are allowed',
    deleteModeTitle: 'Delete Gateway',
    renameModeTitle: 'Edit Gateway name',
    cancelButton: 'Cancel',
    deleteButton: 'Yes, delete it',
    applyButton: 'Apply changes'
  },

  // ─── Delete Gateway Modal ─────────────────────────────────────────────────────
  deleteGateway: {
    title: 'Warning',
    body: 'Deleting the Gateway will revoke all access to the usage data, ability to monitor and control the Gateway. Are you sure you want to delete?',
    cancelButton: 'Cancel',
    applyButton: 'Apply changes'
  },

  // ─── Edit Manage Groups Modal ─────────────────────────────────────────────────
  editManageGroups: {
    editTitle: 'Edit Group',
    createTitle: 'Create Group',
    deleteTitle: 'Warning',
    groupNameLabel: '* Group name',
    groupNamePlaceholder: 'Enter Group name',
    groupNameRequired: 'Group name is required.',
    groupNameMin: 'Group name must be at least 4 characters.',
    groupNameMax: 'Group name must not exceed 25 characters.',
    groupNameExists: 'This group name already exists.',
    breakersLabel: '* Add breakers',
    breakersRequired: 'At least one breaker must be selected.',
    deleteBody: 'Are you sure you want to delete this group?',
    applyButton: 'Apply Changes',
    createButton: 'Create',
    deleteConfirmButton: 'Yes',
    cancelButton: 'Cancel'
  },

  // ─── Manage Groups ────────────────────────────────────────────────────────────
  manageGroups: {
    heading: 'Manage Groups',
    createButton: 'Create group',
    groupNameHeader: 'Group Name',
    breakersHeader: 'Breakers'
  },

  // ─── Notifications Settings ───────────────────────────────────────────────────
  notificationSettings: {
    heading: 'Notification',
    emailColumn: 'Email',
    notificationColumn: 'Notification',
    items: {
      notification1: 'Notification 1',
      gatewayOffline: 'Gateway offline',
      newPeak: 'New peak',
      topConsumers: 'Top consumers'
    }
  },

  // ─── Selected Gateway Layout ──────────────────────────────────────────────────
  selectedGatewayLayout: {
    greeting: name => `Hello ${name}`,
    noDevicesHeading: 'No Inhab devices have been added to this account.',
    noDevicesBody:
      'Please use the Inhab Control mobile application to add your Inhab devices.',
    backgroundAlt: 'Background',
    knowMore: 'Know more',
    aboutInhab: 'About Inhab',
    otherProducts: 'Other Siemens Products',
    faqs: 'FAQs'
  },

  // ─── Reset Password Callback ──────────────────────────────────────────────────
  resetPassword: {
    redirectMsg: "Signing out... You'll be redirected to reset your password shortly."
  },


  // ─── Constants (App_Constants) ────────────────────────────────────────────────
  appConstants: {
    userNameSuccessHeader: 'User name updated',
    userNameSuccessContent:
      'Your user name has been updated successfully. You will be logged out now. Please log in again.',
    userNameFailureHeader: 'Update failed',
    userNameFailureContent:
      'We could not update your user name. Please try again or contact support.',
    passwordSuccessHeader: 'Password updated',
    passwordSuccessContent:
      'Your password has been changed successfully. Please use your new password to log in.',
    passwordFailureHeader: 'Update failed',
    passwordFailureContent:
      'We could not update your password. Please ensure that your current password is correct and try again. If the issue persists, contact support.',
    forgotPasswordRedirectMsg:
      "Signing out... You'll be redirected to reset your password shortly."
  },

  // ─── Consumption Card ───────────────────────────────────────────────────────
  consumptionCard: {
    totalConsumption: 'Total consumption',
    up: 'Up',
    down: 'Down',
    same: 'Same',
    comparisonBy: (direction, percentage) => `${direction} by ${percentage}%`,
    comparedToLast: timestamp => `compared to last ${timestamp}`
  },

  // ─── Top Consumer ───────────────────────────────────────────────────────────
  topConsumer: {
    heading: count => `Top ${count} consumers`
  },

  // ─── Breaker Online Card ────────────────────────────────────────────────────
  breakerOnlineCard: {
    heading: 'Breakers',
    noBreakers: 'No breakers attached to gateway'
  },

  // ─── Toggle Button ──────────────────────────────────────────────────────────
  toggleButton: {
    standby: 'Off',
    offline: 'Offline',
    on: 'On',
    labelOn: 'on',
    labelOff: 'off'
  },

  // ─── Date Range Picker ──────────────────────────────────────────────────────
  dateRangePicker: {
    days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    startDate: 'Start Date',
    endDate: 'End Date',
    dateFormat: 'DD.MM.YYYY'
  },

  // ─── Line Chart ─────────────────────────────────────────────────────────────
  lineChart: {
    noCurrentTitle: 'No current',
    standbyContent: 'The breaker is off.'
  },

  // ─── Footer (extended) ──────────────────────────────────────────────────────
  footerVersion: {
    version: (ver, year) => `Version ${ver}  © ${year} Siemens AG`
  },

  // ─── Download Modal (extended) ──────────────────────────────────────────────
  downloadModalExtra: {
    unavailable: 'Download feature is currently unavailable.'
  },

  // ─── Manage Gateways Errors ─────────────────────────────────────────────────
  manageGatewayErrors: {
    renameFailed: 'Some problem occurred, please try again',
    deleteFailed: 'Failed to delete gateway'
  }

};

export default en;
